// Warehouses (CR_Manager_vs_User_role §5.2; was the Sites page): the company's own yards and warehouses as cards.
// Project and customer sites live under their project. Managers add a warehouse here and edit one from its card.
import {
  Button,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { placeKind } from '@fleetspoke/core/places';
import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { canManage, useSession } from '../auth/session';
import { Empty, Loading, LoadError, Page } from '../components/Page';
import { SiteCard } from '../components/place/SiteCard';
import { keys, useSites } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import { useCountStatus } from '../data/workflow';
import { placePath } from '../lib/places';

// the dialog's map (MapLibre + Draw) loads only when a manager opens it
const SiteDialogLoader = lazy(() =>
  import('../components/SiteDialog').then((m) => ({ default: m.SiteDialogLoader })),
);

export function WarehousesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const manager = canManage(me);
  const sites = useSites();
  const countStatus = useCountStatus();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  useLiveInvalidate('warehouses', ['asset_presence', 'transfers', 'gateways'], [keys.sites]);
  const countOf = new Map((countStatus.data ?? []).map((c) => [c.site_id, c]));
  const warehouses = (sites.data ?? []).filter((s) => placeKind(s.type) === 'warehouse');
  const needle = search.trim().toLowerCase();
  const list = warehouses
    .filter(
      (s) => (showInactive || s.is_active) && (!needle || s.name.toLowerCase().includes(needle)),
    )
    .sort((a, b) => Number(!a.is_active) - Number(!b.is_active) || a.name.localeCompare(b.name));

  return (
    <Page
      title={t('warehouses.title')}
      actions={
        manager ? (
          <Button variant="contained" onClick={() => setAdding(true)}>
            {t('warehouses.new')}
          </Button>
        ) : undefined
      }
    >
      {sites.isLoading ? (
        <Loading />
      ) : sites.error ? (
        <LoadError error={sites.error} onRetry={() => void sites.refetch()} />
      ) : (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
                htmlInput: { 'aria-label': t('common.search') },
              }}
            />
            {warehouses.some((s) => !s.is_active) ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                }
                label={t('places.show_inactive')}
              />
            ) : null}
          </Stack>
          {!list.length ? (
            <Empty>{warehouses.length ? t('places.no_match') : t('warehouses.empty')}</Empty>
          ) : (
            <Grid container spacing={2}>
              {list.map((s) => (
                <Grid key={s.site_id} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <SiteCard
                    site={s}
                    count={countOf.get(s.site_id)}
                    manager={manager}
                    onOpen={() => void navigate(placePath(s))}
                    onEdit={manager ? () => void navigate(`${placePath(s)}?edit=1`) : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      {adding ? (
        <Suspense fallback={<Loading />}>
          <SiteDialogLoader kind="warehouse" onClose={() => setAdding(false)} />
        </Suspense>
      ) : null}
    </Page>
  );
}
